import { memo, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { AnimatedSpinner } from '../../../../components/AnimatedSpinner'
import { UploadIcon } from '../../../../components/Icons'
import { useTwinData } from '../../../../hooks/storeHooks/useTwin'
import { Faq } from './FaqTab'

const parseCsv = (csvText: string): Faq[] => {
  const lines = csvText.trim().split('\n')
  if (lines.length < 2) return []

  // Skip header row and parse data rows
  const faqs: Faq[] = []

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) continue

    // Parse CSV line handling quoted fields
    const fields = parseCSVLine(line)
    if (fields.length >= 2) {
      faqs.push({
        question: fields[0],
        answer: fields[1],
        id: crypto.randomUUID(),
        isNew: true,
      })
    }
  }

  return faqs
}

const parseCSVLine = (line: string): string[] => {
  const fields: string[] = []
  let current = ''
  let insideQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    const nextChar = line[i + 1]

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        // Escaped quote
        current += '"'
        i++
      } else {
        // Toggle quote state
        insideQuotes = !insideQuotes
      }
    } else if (char === ',' && !insideQuotes) {
      // Field separator
      fields.push(current)
      current = ''
    } else {
      current += char
    }
  }

  fields.push(current)
  return fields
}

const CsvUpload: React.FC = memo(() => {
  const { t } = useTranslation()
  const { faqs, setFaqs } = useTwinData()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.appBridge
    ) {
      const payload = {
        action: 'files/filter',
        source: ['text/csv'],
        allowsMultipleSelection: false,
      }
      window.webkit.messageHandlers.appBridge.postMessage(payload)
    } else {
      console.warn('Native bridge not available (not running in the app?)')
    }
  }, [])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsLoading(true)

    const reader = new FileReader()
    reader.onload = e => {
      try {
        const csvText = e.target?.result as string
        const parsedFaqs = parseCsv(csvText)

        if (parsedFaqs.length === 0) {
          toast.error(t('twin.noValidFaq'))
          setIsLoading(false)
          return
        }

        setFaqs([...faqs, ...parsedFaqs])
        toast.error(null)

        if (inputRef.current) {
          inputRef.current.value = ''
        }
        setIsLoading(false)
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to parse CSV file'
        toast.error(errorMessage)
        setIsLoading(false)
      }
    }
    reader.readAsText(file)
  }

  return (
    <div className="flex flex-col gap-2">
      <label
        className={`btn btn-primary ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} whitespace-nowrap`}
        htmlFor="csv-file"
      >
        <input
          type="file"
          accept=".csv"
          id="csv-file"
          className="pointer-events-none opacity-0 hidden"
          onChange={handleFileChange}
          ref={inputRef}
          disabled={isLoading}
        />
        {isLoading ? (
          <>
            <AnimatedSpinner className="mr-2" />
            {t('twin.processing')}
          </>
        ) : (
          <>
            <UploadIcon className="mr-2" />
            {t('twin.uploadCsv')}
          </>
        )}
      </label>
    </div>
  )
})

export { CsvUpload }
